import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Popover,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import fileDownload from 'js-file-download';
import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import {
  deleteShippingCode,
  exportShippingCodes,
  getShippingCodes,
  ShippingCodeDto,
  updateShippingCode,
} from '../../felles/APICalls_NE01';
import NE01DeleteDialog from './NE01DeleteDialog';

const CARRIER_NAME_MAX_LENGTH = 15;

interface NE01ForsendelseskoderProps {
  size: { width: number; height: number };
}

// Lys, avrundet knapp som i skissen. color="inherit" alene gir svart kant (currentColor).
const outlinedButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  color: 'text.primary',
  bgcolor: 'background.paper',
  borderColor: 'grey.300',
  borderRadius: 1.5,
  '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.50' },
  '&.Mui-disabled': { borderColor: 'grey.200' },
} as const;

const extractErrorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.message || err?.response?.data?.error || fallback;

/** Søk på kode eller transportørnavn, uten hensyn til store/små bokstaver. */
export const matchesSearch = (row: ShippingCodeDto, search: string): boolean => {
  const term = search.trim().toLocaleLowerCase('nb-NO');
  return term === ''
    || row.code.toLocaleLowerCase('nb-NO').includes(term)
    || row.carrierName.toLocaleLowerCase('nb-NO').includes(term);
};

export default function NE01Forsendelseskoder({ size }: NE01ForsendelseskoderProps) {
  const [rows, setRows] = useState<ShippingCodeDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState<ShippingCodeDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getShippingCodes();
        if (cancelled) return;
        if (response.data.found) {
          setRows(response.data.items);
        } else {
          setLoadError(response.data.message || 'Kunne ikke hente forsendelseskoder');
        }
      } catch (err: any) {
        if (!cancelled) setLoadError(extractErrorMessage(err, 'Kunne ikke hente forsendelseskoder'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredRows = useMemo(() => rows.filter((row) => matchesSearch(row, search)), [rows, search]);

  const replaceRow = (code: string, carrierName: string) =>
    setRows((current) => current.map((row) => (row.code === code ? { ...row, carrierName } : row)));

  const startEdit = (row: ShippingCodeDto) => {
    setEditingCode(row.code);
    setEditValue(row.carrierName);
    setEditError('');
  };

  const cancelEdit = () => {
    setEditingCode(null);
    setEditValue('');
    setEditError('');
  };

  const saveEdit = async () => {
    if (editingCode === null || isSaving) return;
    setIsSaving(true);
    try {
      const response = await updateShippingCode(editingCode, editValue.trim());
      if (response.data.updated) {
        replaceRow(response.data.code, response.data.carrierName);
        cancelEdit();
        setSnackbar({ message: response.data.message || 'Forsendelseskode lagret', severity: 'success' });
      } else {
        setEditError(response.data.message || 'Kunne ikke lagre');
      }
    } catch (err: any) {
      setEditError(extractErrorMessage(err, 'Kunne ikke lagre'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveEdit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEdit();
    }
  };

  const confirmDelete = async () => {
    if (deleteRow === null) return;
    setIsDeleting(true);
    try {
      const response = await deleteShippingCode(deleteRow.code);
      if (response.data.deleted) {
        replaceRow(deleteRow.code, '');
        setSnackbar({ message: response.data.message, severity: 'success' });
      } else {
        setSnackbar({ message: response.data.message || 'Kunne ikke slette', severity: 'error' });
      }
      setDeleteRow(null);
    } catch (err: any) {
      setSnackbar({ message: extractErrorMessage(err, 'Kunne ikke slette'), severity: 'error' });
      setDeleteRow(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await exportShippingCodes(search.trim());
      fileDownload(response.data, 'forsendelseskoder.xlsx');
    } catch (err: any) {
      setSnackbar({ message: extractErrorMessage(err, 'Feil ved eksport til Excel'), severity: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  const tableMaxHeight = Math.max(size.height - 230, 300);

  return (
    <Box sx={{ m: 2, p: 3, maxWidth: 1000, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            Forsendelseskoder
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Oversikt over forsendelseskoder og tilhørende transportører
          </Typography>
        </Box>
        <IconButton aria-label="Hjelp" onClick={(e) => setHelpAnchor(e.currentTarget)} color="success">
          <InfoOutlinedIcon />
        </IconButton>
        <Popover
          open={helpAnchor !== null}
          anchorEl={helpAnchor}
          onClose={() => setHelpAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {/* TODO: endelig hjelpetekst kommer fra PO. */}
          <Box sx={{ p: 2, maxWidth: 360 }}>
            <Typography variant="subtitle2" gutterBottom>
              Forsendelseskoder
            </Typography>
            <Typography variant="body2">
              Hver kode (A–Å, 0–9 og spesialtegn) kan knyttes til en transportør på inntil 15 tegn.
              Slett fjerner transportøren, men koden blir stående i listen.
            </Typography>
          </Box>
        </Popover>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Søk på kode eller transportør…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', borderRadius: 1.5 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
            htmlInput: { 'aria-label': 'Søk på kode eller transportør' },
          }}
        />
        <Button
          variant="outlined"
          startIcon={isExporting ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />}
          onClick={handleExport}
          disabled={isExporting || isLoading || rows.length === 0}
          sx={{ ...outlinedButtonSx, whiteSpace: 'nowrap', px: 2.5 }}
        >
          Eksporter til Excel
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Viser {filteredRows.length} av {rows.length} koder
      </Typography>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <TableContainer sx={{ maxHeight: tableMaxHeight, border: 1, borderColor: 'grey.300', borderRadius: 2, bgcolor: 'background.paper' }}>
        <Table stickyHeader size="small" aria-label="Forsendelseskoder">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: 'background.paper', width: 120 }}>Kode</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: 'background.paper' }}>Transportør</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: 'background.paper', width: 260 }}>Handlinger</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && filteredRows.length === 0 && rows.length > 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Ingen koder samsvarer med søket
                </TableCell>
              </TableRow>
            )}
            {filteredRows.map((row) => {
              const isEditing = editingCode === row.code;
              const otherRowEditing = editingCode !== null && !isEditing;
              return (
                <TableRow key={row.code} hover>
                  <TableCell sx={{ fontWeight: 700, py: 1.5 }}>{row.code}</TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        autoFocus
                        fullWidth
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value.toLocaleUpperCase('nb-NO'))}
                        onKeyDown={handleEditKeyDown}
                        error={editError !== ''}
                        helperText={editError}
                        disabled={isSaving}
                        slotProps={{
                          htmlInput: {
                            maxLength: CARRIER_NAME_MAX_LENGTH,
                            'aria-label': `Transportør for kode ${row.code}`,
                          },
                        }}
                      />
                    ) : (
                      row.carrierName
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {isEditing ? (
                        <>
                          <Button variant="contained" onClick={saveEdit} disabled={isSaving} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}>
                            Lagre
                          </Button>
                          <Button variant="outlined" onClick={cancelEdit} disabled={isSaving} sx={outlinedButtonSx}>
                            Avbryt
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outlined"
                            startIcon={<EditOutlinedIcon fontSize="small" />}
                            onClick={() => startEdit(row)}
                            disabled={otherRowEditing}
                            aria-label={`Rediger kode ${row.code}`}
                            sx={outlinedButtonSx}
                          >
                            Rediger
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<DeleteOutlineIcon fontSize="small" />}
                            onClick={() => setDeleteRow(row)}
                            disabled={otherRowEditing || row.carrierName === ''}
                            aria-label={`Slett kode ${row.code}`}
                            sx={outlinedButtonSx}
                          >
                            Slett
                          </Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <NE01DeleteDialog
        row={deleteRow}
        isDeleting={isDeleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteRow(null)}
      />

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity ?? 'success'} onClose={() => setSnackbar(null)} variant="filled">
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
